import { useFormContext } from 'react-hook-form';
import removeMarkdown from 'remove-markdown';
import { RecordFilter, TalkFlowChatSourceScope } from '@/types/api';
import { isEmptyContent } from '@/utils/editor';
import { ensureLexicalHtml } from '@/components/Document/@hooks/useAiSummaries';
import DocumentAgentIcon from '@/components/Document/DocumentAgentIcon';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { useRecorder } from '@/components/RecorderProvider';
import AiChatView, {
  AiChatActionButton,
  AiChatAssistantMessage,
  ChevronIcon,
} from '@/components/@IntranetLibrary/AiChatView';
import Image from 'next/image';
import useTalkFlowChat from './useTalkFlowChat';

type Props = {
  closeList: () => void;
  documentId: string;
  className?: string;
  isOwner?: boolean;
  onInsertToContent?: (text: string, mode: 'append' | 'overwrite') => void;
  // 모바일: 헤더를 왼쪽 뒤로가기 버튼 + 가운데 타이틀 형태로 렌더
  isMobile?: boolean;
};

// 답변 근거 범위 — 선택 UI(입력창 pill)가 디자인에서 빠져 항상 음성 파일+본문으로 보낸다
const SCOPE: TalkFlowChatSourceScope = 'recording_and_content';

const GREETING = {
  empty: {
    sub: '회의 내용이 있어야 질문할 수 있어요.',
    main: '녹음을 하거나 메모를 작성해 주세요.',
  },
  recording: {
    sub: '녹음중엔 질문할 수 없어요.',
    main: '녹음을 마무리하신 뒤 질문을 전달해 주세요.',
  },
  ready: {
    sub: '만나서 반갑습니다!',
    main: '무엇을 도와드릴까요?',
  },
};

const SUGGESTIONS = [
  '핵심 문제는 뭐였어?',
  '공유를 위해 요약해줘',
  '다음에 해야할 것은 뭐야?',
  '결정된 사항은 뭐였어?',
];

const AiChat = ({
  closeList,
  documentId,
  className,
  isOwner,
  onInsertToContent,
  isMobile,
}: Props) => {
  const { watch } = useFormContext<RecordFilter>();
  const { showSuccess, showError } = useSnackbar();
  const dialog = useDialog();
  const {
    messages,
    isSending,
    isInitializing,
    send,
    stop,
    resetChat,
    loadMore,
    hasMore,
    isLoadingMore,
  } = useTalkFlowChat(documentId);

  const recordings = watch('recordings');
  const memo = watch('memo');
  const content = watch('content');

  // 비활성 조건 (기획 4.3): (1) 녹음·메모 모두 없음 (2) 녹음 중 (3) 정리 생성 중
  // 녹음 상태는 전역(RecorderProvider)이라 다른 문서 녹음 중에도 막히던 문제 → 이 문서를 녹음 중일 때만 제한 (PORTAL-13364)
  const { isRecordingInProgress: isAnyRecording, state: recorderState } =
    useRecorder();
  const isRecordingInProgress =
    isAnyRecording && recorderState.docId === documentId;

  const hasContent =
    recordings.some((r) => r.state === 'complete') ||
    !isEmptyContent(memo ?? '') ||
    !isEmptyContent(content ?? '');

  const disabled = !hasContent || isRecordingInProgress;

  const noticeText = isRecordingInProgress
    ? '녹음을 마무리하신 뒤 질문을 전달해 주세요.'
    : '정리할 내용이 없어요. 내용이나 음성파일을 업로드해 주세요.';

  // 대화 시작 전 인사말 — 질문할 수 없는 이유(내용 없음/녹음 중)를 상태별로 안내한다
  const greeting = isRecordingInProgress
    ? GREETING.recording
    : hasContent
    ? GREETING.ready
    : GREETING.empty;

  const hasMessages = messages.length > 0;
  // 지울 대화가 없거나, 답변 생성 중(pending 말풍선이 남아 꼬임)이면 초기화 불가
  const resetDisabled = !hasMessages || isSending || isInitializing;

  const handleCopy = async (answer: string) => {
    try {
      // 마크다운 원문을 그대로 복사하면 에디터에 붙여도 **·# 기호가 남는다.
      // HTML(서식 변환본)과 평문(기호 제거본)을 함께 실어 붙는 곳에 맞는 형식이 들어가게 한다.
      const plain = removeMarkdown(answer);
      if (typeof ClipboardItem === 'undefined') {
        await navigator.clipboard.writeText(plain);
      } else {
        const html = await ensureLexicalHtml(answer);
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plain], { type: 'text/plain' }),
          }),
        ]);
      }
      showSuccess({ message: '복사되었습니다.' });
    } catch {
      showError({ message: '복사에 실패했습니다.' });
    }
  };

  // 'AI 정리'에 이어 붙이기 — content 뒤에 이어붙임
  const handlePasteBelow = (answer: string) => {
    onInsertToContent?.(answer, 'append');
    showSuccess({ message: "'AI 정리'에 이어 붙였어요." });
  };

  const handleReset = () => {
    dialog.show({
      message: '채팅을 초기화할까요?\n지금까지의 채팅 내용이 모두 삭제됩니다.',
      confirmButtonText: '초기화',
      cancelButtonText: '취소',
      onConfirm: resetChat,
    });
  };

  // 'AI 정리'에 덮어쓰기 — 파괴적이므로 확인 다이얼로그
  const handleOverwrite = (answer: string) => {
    dialog.show({
      message:
        "'AI 정리' 탭에 내용을 덮어쓸까요?\n기존 내용이 새 내용으로 교체됩니다.",
      confirmButtonText: '덮어쓰기',
      cancelButtonText: '취소',
      onConfirm: () => {
        onInsertToContent?.(answer, 'overwrite');
        showSuccess({ message: "'AI 정리'를 덮어썼어요." });
      },
    });
  };

  const titleIcon = (
    <Image
      src="/images/ai-summary-star.png"
      alt=""
      width={24}
      height={24}
      priority
    />
  );

  const renderActions = (m: AiChatAssistantMessage) => (
    <>
      {isOwner && (
        <>
          <AiChatActionButton
            tooltip="'AI 정리'에 이어 붙이기"
            onClick={() => handlePasteBelow(m.text)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M6.77352 5.10352L17.0859 5.10352M6.77352 8.15037H17.0859M12.3985 11.1972L17.0859 11.1972M12.3985 14.2441H17.0859M7.24256 14.4786L10.055 12.7208L7.24228 10.963M5.31868 10.9154H3.70187M3.70187 10.9154H2.08594M3.70187 10.9154V12.5313M3.70187 10.9154V9.29854"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </AiChatActionButton>
          <AiChatActionButton
            tooltip="'AI 정리'에 덮어쓰기"
            onClick={() => handleOverwrite(m.text)}
          >
            <svg width="20" height="20" viewBox="11 10 14 16" fill="none">
              <path
                d="M15.2617 14.0336H18.7099M15.2617 15.7577H18.7099M15.2617 17.4818H16.9858M16.3274 25.3986L17.5421 24.1768L16.2699 22.8986M17.5421 24.1768H14.4171M22.1665 14.2332H23.054C23.6063 14.2332 24.054 14.6809 24.054 15.2332V22.4299C24.054 22.9821 23.6063 23.4299 23.054 23.4299H19.7149M19.7214 20.93H14.2502C13.6979 20.93 13.2502 20.4823 13.2502 19.93L13.25 12.7356C13.25 12.1833 13.6976 11.7356 14.2498 11.7355L19.7212 11.7346C20.2735 11.7345 20.7214 12.1822 20.7214 12.7346V19.93C20.7214 20.4823 20.2736 20.93 19.7214 20.93Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </AiChatActionButton>
        </>
      )}
      <AiChatActionButton tooltip="복사하기" onClick={() => handleCopy(m.text)}>
        <svg width="20" height="20" viewBox="8 8 20 20" fill="none">
          <path
            d="M12.3257 24.8741H20.4965C20.6632 24.8741 20.823 24.8079 20.9409 24.69C21.0588 24.5721 21.125 24.4122 21.125 24.2455V16.0747L18.6109 13.5605H12.9542C12.7875 13.5605 12.6277 13.6268 12.5098 13.7446C12.3919 13.8625 12.3257 14.0224 12.3257 14.1891V24.8741Z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M21.7547 22.4405H23.0434C23.21 22.4405 23.3699 22.3743 23.4878 22.2564C23.6057 22.1385 23.6719 21.9786 23.6719 21.8119V13.6411L21.1578 11.127H15.5011C15.3344 11.127 15.1746 11.1932 15.0567 11.311C14.9388 11.4289 14.8726 11.5888 14.8726 11.7555V13.0624"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </AiChatActionButton>
    </>
  );

  return (
    <AiChatView
      className={className}
      variant="light"
      isMobile={isMobile}
      header={{
        variant: isMobile ? 'mobile' : 'full',
        title: 'AI Chat',
        titleIcon,
        onClose: closeList,
        onReset: handleReset,
        resetDisabled,
        closeIcon: <ChevronIcon />,
      }}
      data={{
        messages,
        isSending,
        isInitializing,
        onSend: (t) => send(t, SCOPE),
        onStop: stop,
        loadMore,
        hasMore,
        isLoadingMore,
      }}
      greeting={greeting}
      disclaimer="AI 답변은 실제 내용과 다를 수 있으니 결과를 확인해 주세요."
      placeholder="AI에게 질문이나 요구사항을 입력해 보세요"
      suggestions={SUGGESTIONS}
      disabled={disabled}
      noticeText={noticeText}
      emptyIcon={<DocumentAgentIcon width={80} height={80} transparent />}
      renderAvatar={(isStreaming) =>
        isStreaming ? (
          <DocumentAgentIcon width={28} height={28} transparent />
        ) : (
          <Image
            src="/images/ai-summary-star.png"
            alt=""
            width={28}
            height={28}
          />
        )
      }
      renderActions={renderActions}
    />
  );
};

export default AiChat;
